import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faTrash, faLink } from '@fortawesome/free-solid-svg-icons';
import config from '../../configs/config';
import './styles/RedirectModal.css';

const RedirectModal = ({ isOpen, onClose, projectId, onSave }) => {
  const [redirects, setRedirects] = useState({ screenout: '', terminate: '', quotafull: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ─── Load Redirects (PGAdmin only) ───
  useEffect(() => {
    if (!isOpen || !projectId) return;
    
    const loadRedirects = async () => {
      setLoading(true);
      try {
        // ✅ FIXED: Correct endpoint URL
        const res = await fetch(`${config.API_BASE_URL}/get-redirect-config/${projectId}`, {
          credentials: 'include',
        });
        const result = await res.json();
        
        if (result.success && result.redirects) {
          setRedirects({
            screenout: result.redirects.screenout || '',
            terminate: result.redirects.terminate || '',
            quotafull: result.redirects.quotafull || '',
          });
        } else {
          setRedirects({ screenout: '', terminate: '', quotafull: '' });
        }
      } catch (err) {
        console.error('Failed to load redirects:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadRedirects();
  }, [isOpen, projectId]);

  // ─── Handle input change ───
  const handleChange = (key, value) => {
    setRedirects(prev => ({ ...prev, [key]: value }));
  };

  // ─── Save Redirects (PGAdmin only - preserves quotas) ───
  const handleSave = async () => {
    if (!redirects.screenout && !redirects.terminate && !redirects.quotafull) {
      if (!window.confirm('All redirect URLs are empty. Continue?')) {
        return;
      }
    }
    
    setSaving(true);
    try {
      // ✅ Use dedicated redirect save endpoint
      const saveRes = await fetch(`${config.API_BASE_URL}/save-redirect-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          projectId,
          redirects: {
            screenout: redirects.screenout || '',
            terminate: redirects.terminate || '',
            quotafull: redirects.quotafull || '',
          }
        }),
      });
      
      const saveResult = await saveRes.json();
      
      if (saveResult.success) {
        onSave?.(redirects);
        onClose();
      } else {
        alert('Failed to save redirects: ' + (saveResult.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error saving redirects:', err);
      alert('Error saving redirects');
    } finally {
      setSaving(false);
    }
  };

  // ─── Clear all redirects ───
  const handleClear = () => {
    if (window.confirm('Clear all redirect URLs?')) {
      setRedirects({ screenout: '', terminate: '', quotafull: '' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="redirect-modal-overlay" onClick={onClose}>
      <div className="redirect-modal-content" onClick={e => e.stopPropagation()}>
        <div className="redirect-modal-header">
          <div className="redirect-header-left">
            <h3>
              <FontAwesomeIcon icon={faLink} style={{ marginRight: "8px" }} />
              Redirect URLs
            </h3>
          </div>
          <div className="redirect-header-right">
            <button className="redirect-close-btn" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
        
        <div className="redirect-modal-body">
          {loading ? (
            <p className="redirect-loading">Loading redirects...</p>
          ) : (
            <div className="redirect-fields">
              <div className="redirect-field">
                <label>Screenout URL</label>
                <input
                  type="text"
                  value={redirects.screenout || ''}
                  onChange={e => handleChange('screenout', e.target.value)}
                  placeholder="https://example.com/screenout"
                />
                <small className="redirect-hint">
                  Users will be redirected here when screened out
                </small>
              </div>
              
              <div className="redirect-field">
                <label>Terminate URL</label>
                <input
                  type="text"
                  value={redirects.terminate || ''}
                  onChange={e => handleChange('terminate', e.target.value)}
                  placeholder="https://example.com/terminate"
                />
                <small className="redirect-hint">
                  Users will be redirected here when terminated
                </small>
              </div>
              
              <div className="redirect-field">
                <label>Quota Full URL</label>
                <input
                  type="text"
                  value={redirects.quotafull || ''}
                  onChange={e => handleChange('quotafull', e.target.value)}
                  placeholder="https://example.com/quotafull"
                />
                <small className="redirect-hint">
                  Users will be redirected here when quota is full
                </small>
              </div>
            </div>
          )}
        </div>
        
        <div className="redirect-modal-footer">
          <button 
            className="redirect-clear-btn" 
            onClick={handleClear}
            disabled={loading || saving}
          >
            <FontAwesomeIcon icon={faTrash} /> Clear All
          </button>
          <button 
            className="redirect-cancel-btn" 
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            className="redirect-save-btn" 
            onClick={handleSave} 
            disabled={saving || loading}
          >
            {saving ? (
              'Saving...'
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} /> Save Redirects
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RedirectModal;